import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';
import callApi from 'hooks/useApi';
import { useOrganization } from 'contexts/OrganizationContext';
import { computeCloudRestApiEnabled } from 'utils/cloudRestApiAccess';
import { invalidateSupportedFeatureCache } from 'utils/supportedFeatures';
import { markFeatureListSettled } from 'utils/featureListSettled';

const cookies = new Cookies();

function readInsightSessionSignature() {
  const sessionId = cookies.get('_Id');
  const accountId = cookies.get('accountId') ?? '';
  return `${sessionId ?? ''}|${accountId}`;
}

/**
 * Cookies are not reactive in React — changes to `_Id` / `accountId` do not rerender.
 * Keep a signature string in state and bump it when navigation, focus/visibility, or a
 * lightweight poll detects a change so Cloud REST access can be recomputed in-session.
 */
function useInsightSessionSignature() {
  const location = useLocation();
  const [signature, setSignature] = useState(readInsightSessionSignature);

  useEffect(() => {
    const next = readInsightSessionSignature();
    setSignature((prev) => (prev === next ? prev : next));
  }, [location.pathname, location.search, location.key]);

  useEffect(() => {
    let last = readInsightSessionSignature();
    let intervalId;
    const sync = () => {
      const next = readInsightSessionSignature();
      if (next !== last) {
        last = next;
        setSignature(next);
      }
    };
    const runIntervalWhileVisible = () => {
      window.clearInterval(intervalId);
      if (document.visibilityState !== 'visible') return;
      intervalId = window.setInterval(sync, 4000);
    };
    const onVisibilityOrFocus = () => {
      sync();
      runIntervalWhileVisible();
    };
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    runIntervalWhileVisible();
    return () => {
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.clearInterval(intervalId);
    };
  }, []);

  return signature;
}

const CloudRestApiAccessContext = createContext({
  loading: true,
  enabled: false
});

/**
 * Loads supported features (same contract as legacy Admin portal), caches alongside
 * `featureSupportAdminCheck`, and exposes whether Cloud REST API UI should show.
 *
 * Re-evaluates when org/scope changes (OrganizationContext), when the route changes, or when
 * `_Id` / `accountId` cookies change (see useInsightSessionSignature). Cache entries include
 * sessionId + accountId + org scope key.
 */
export function CloudRestApiAccessProvider({ children }) {
  const { orgId, scopeId, scopeResolved } = useOrganization();
  const insightSessionSignature = useInsightSessionSignature();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Retry transient / CORS / network failures instead of giving up after one try.
    async function fetchDetailsWithRetry(attempts = 3) {
      let lastErr;
      for (let i = 0; i < attempts; i += 1) {
        try {
          const result = await callApi('GET_SUPPORTED_FEATURE_LIST', {}, {});
          return result?.details ?? null;
        } catch (err) {
          lastErr = err;
          await new Promise((r) => setTimeout(r, 400 * (i + 1))); // small backoff
        }
      }
      throw lastErr;
    }

    async function run() {
      const sessionId = cookies.get('_Id');
      const accountId = cookies.get('accountId') ?? '';
      const orgKey = `${scopeId ?? ''}:${orgId ?? ''}`;

      if (!sessionId) {
        if (!cancelled) {
          markFeatureListSettled(true);
          setLoading(false);
          setEnabled(false);
        }
        return;
      }

      // Don't fetch or stamp the feature list against an unresolved scope. This is what let
      // the cache get keyed to the transient MSP scope ("0:") and then mismatch the real
      // location scope. Keep whatever we have until scope settles.
      if (!scopeResolved) return;

      try {
        const featuresRaw = localStorage.getItem('featureSupportAdminCheck');
        const metaRaw = localStorage.getItem('featureSupportAdminMeta');
        let meta = {};
        try {
          meta = JSON.parse(metaRaw || '{}');
        } catch {
          meta = {};
        }

        const sameSession = meta.sessionId === sessionId && String(meta.accountId ?? '') === String(accountId);
        const cacheMatchesContext = Boolean(featuresRaw && metaRaw) && sameSession && String(meta.orgKey ?? '') === String(orgKey);

        let details = null;
        let usedCache = false;
        if (cacheMatchesContext) {
          try {
            const parsed = JSON.parse(featuresRaw);
            if (Array.isArray(parsed)) {
              details = { supportedFeatures: parsed, cloudRESTSupport: meta.cloudRESTSupport };
              usedCache = true;
            }
          } catch {
            details = null;
          }
        }

        if (!details) {
          if (!cancelled) {
            markFeatureListSettled(false);
            setLoading(true);
          }

          // Cross-account/session change: the cached list belongs to a different account,
          // so it must NOT be shown while we refetch (prevents stale-account leakage into
          // feature gates, e.g. the ORBI device detail page). Only clear eagerly in that case.
          if (!sameSession) {
            localStorage.removeItem('featureSupportAdminCheck');
            localStorage.removeItem('featureSupportAdminMeta');
            invalidateSupportedFeatureCache();
          }

          // Same account: do NOT delete the list before the refetch. A failed / CORS-blocked
          // GET_SUPPORTED_FEATURE_LIST used to leave the app with no list, so every
          // feature-gated menu item and the dashboard body hid themselves.
          let fresh = null;
          try {
            fresh = await fetchDetailsWithRetry();
          } catch {
            fresh = null;
          }
          if (cancelled) return;

          if (fresh) {
            localStorage.setItem('featureSupportAdminCheck', JSON.stringify(fresh.supportedFeatures || []));
            localStorage.setItem(
              'featureSupportAdminMeta',
              JSON.stringify({
                cloudRESTSupport: fresh.cloudRESTSupport ?? '',
                sessionId,
                accountId,
                orgKey
              })
            );
            invalidateSupportedFeatureCache();
            details = fresh;
          } else if (sameSession && featuresRaw) {
            // Refetch failed for the same account -> fall back to the last good list instead
            // of a blank sidebar / dashboard.
            try {
              const parsed = JSON.parse(featuresRaw);
              if (Array.isArray(parsed)) {
                details = { supportedFeatures: parsed, cloudRESTSupport: meta.cloudRESTSupport };
              }
            } catch {
              details = null;
            }
          }
        }

        if (!cancelled) {
          setEnabled(computeCloudRestApiEnabled(details));
          markFeatureListSettled(true);
          setLoading(false); // always clear loading so feature gates never stay stuck
        }

        // Stale-while-revalidate: a cache hit reflects cloudRESTSupport as of the last fetch
        // for this session/account/org. If the backend toggles the flag mid-session, the cached
        // value would otherwise keep the Cloud REST API menu item / route in whatever state it
        // was in until the account/org context changes (e.g. re-login or switching orgs). Silently
        // re-verify against the backend and correct state if it disagrees — without touching
        // `loading`, so there's no flicker/blank sidebar if this fails.
        if (usedCache && !cancelled) {
          try {
            const revalidated = await fetchDetailsWithRetry(1);
            if (!cancelled && revalidated) {
              localStorage.setItem('featureSupportAdminCheck', JSON.stringify(revalidated.supportedFeatures || []));
              localStorage.setItem(
                'featureSupportAdminMeta',
                JSON.stringify({
                  cloudRESTSupport: revalidated.cloudRESTSupport ?? '',
                  sessionId,
                  accountId,
                  orgKey
                })
              );
              invalidateSupportedFeatureCache();
              setEnabled(computeCloudRestApiEnabled(revalidated));
            }
          } catch {
            // Keep the cached value if the background revalidation fails.
          }
        }
      } catch {
        if (!cancelled) {
          setEnabled(false);
          markFeatureListSettled(true);
          setLoading(false);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [orgId, scopeId, scopeResolved, insightSessionSignature]);

  const value = useMemo(() => ({ loading, enabled }), [loading, enabled]);

  return <CloudRestApiAccessContext.Provider value={value}>{children}</CloudRestApiAccessContext.Provider>;
}

export function useCloudRestApiAccess() {
  return useContext(CloudRestApiAccessContext);
}

export { useInsightSessionSignature };
